import type {
  ScheduleEntry,
  UpdateScheduleEntryDto,
  UpsertScheduleBreakDto,
} from '@myapp/contract';
import {
  MIN_WORK_MINUTES,
  applyDurationDrag,
  instantsFromSlot,
  overrideFields,
  shiftIso,
  workWindow,
  type DragHandle,
  type Durations,
  type ScheduleOverrides,
  type SegmentHandle,
} from '../../../shared/lib/schedule';
import type { DragPolicy } from './dragPolicy';
import type { BoardBlock } from './boardTypes';

/**
 * Turns a finished drag into the request that commits it. Pure, no React — this is where every
 * "which field does this type actually honour" rule lives.
 */

export type { DragHandle };

export interface DragOutcome {
  handle: DragHandle;
  /** Already snapped to the 15-minute grid. */
  deltaMinutes: number;
  /** Row the pointer ended over. Equal to the origin unless the block crossed rows. */
  technicianId: number;
}

export type SchedulePatch =
  | { kind: 'entry'; entryId: number; dto: UpdateScheduleEntryDto }
  | { kind: 'break'; dto: UpsertScheduleBreakDto };

/**
 * The occupancy components the server actually used to draw this block. Exported because the drag
 * preview has to read the same `actual ?? planned` fields, or the ghost and the patch would disagree
 * on a started appointment.
 */
export function trueDurations(entry: ScheduleEntry): Durations & { workStartsAt: string } {
  return {
    travel: entry.actualTravelMinutes ?? entry.plannedTravelMinutes,
    work: entry.actualWorkMinutes ?? entry.plannedWorkMinutes,
    departure: entry.actualDepartureMinutes ?? entry.plannedDepartureMinutes,
    workStartsAt: workWindow(entry).startsAt,
  };
}

/**
 * Applies one handle's delta to this entry. The arithmetic lives in `applyDurationDrag`, which the
 * ghost also consumes; all this adds is the ISO shift of the work start.
 */
function applyDrag(entry: ScheduleEntry, outcome: DragOutcome) {
  const { workStartsAt, ...base } = trueDurations(entry);
  // `start`/`end` belong to the ABSENCE branch, which returns before reaching here.
  const handle = outcome.handle as 'move' | SegmentHandle;
  const next = applyDurationDrag(base, handle, outcome.deltaMinutes);

  return {
    ...next.durations,
    workStartsAt: shiftIso(workStartsAt, next.workStartDelta),
  };
}

/**
 * `null` when the drag was a no-op — same slot, same technician. Saves a pointless round-trip and,
 * more importantly, avoids a write that would recreate an absence's child entry ids for nothing.
 *
 * `overrides` is what a confirmed 409 dialog sends back: the same patch, plus the flag that tells
 * the server to accept it anyway. `overrideFields` omits unset keys, so an ordinary drag builds a
 * byte-identical request to the one it built before this parameter existed.
 */
export function buildSchedulePatch(
  block: BoardBlock,
  policy: DragPolicy,
  outcome: DragOutcome,
  overrides: ScheduleOverrides = {},
): SchedulePatch | null {
  const { entry } = block;
  const reassigned = policy.canReassign && outcome.technicianId !== entry.technicianId;

  if (outcome.deltaMinutes === 0 && !reassigned) return null;

  // Breaks go through PUT /schedule/break, which is an upsert keyed by technician + start. That
  // route also covers a *projected* break, whose `entry.id` is null and therefore un-PATCHable.
  // It takes `allowOutsideWorkingHours` but not `force`: UpsertBreakUseCase checks working hours
  // and nothing else, so there is no overlap for a force to override.
  if (entry.type === 'BREAK') {
    return {
      kind: 'break',
      dto: {
        technicianId: entry.technicianId,
        startsAt: shiftIso(entry.startsAt, outcome.deltaMinutes),
        ...(overrides.allowOutsideWorkingHours ? { allowOutsideWorkingHours: true } : {}),
      },
    };
  }

  if (entry.id == null) return null;

  const technicianPatch = reassigned ? { technicianId: outcome.technicianId } : {};

  // An absence is a plain span — travel and departure are forced to 0 by the occupancy CHECK.
  if (entry.type === 'ABSENCE') {
    const startsAt =
      outcome.handle === 'end' ? entry.startsAt : shiftIso(entry.startsAt, outcome.deltaMinutes);
    const endsAt =
      outcome.handle === 'start' ? entry.endsAt : shiftIso(entry.endsAt, outcome.deltaMinutes);

    // `endsAt > startsAt` is a DB CHECK; refuse rather than send a request that cannot succeed.
    if (new Date(endsAt).getTime() - new Date(startsAt).getTime() < MIN_WORK_MINUTES * 60_000) {
      return null;
    }

    return {
      kind: 'entry',
      entryId: entry.id,
      dto: {
        slot: { kind: 'SPAN', startsAt, endsAt },
        ...technicianPatch,
        ...overrideFields(overrides),
      },
    };
  }

  const next = applyDrag(entry, outcome);

  // A completed block is drawn from its actuals. Writing `slot` would update the planned values
  // that COALESCE then ignores, and the block would not move.
  //
  // The API takes no Ist-Minuten, only the four Ist-Zeitpunkte, and re-derives the minutes from
  // them — so the drag is laid out as instants, always all four: an absent Anfahrt or Abfahrt goes
  // out as `null`, which clears a stale instant and derives an honest 0 for it. A *running* block
  // never reaches here (`dragPolicy` makes it inert): sending `actualEndsAt` for it would close the
  // technician's session from the board.
  if (policy.writesActuals) {
    const instants = instantsFromSlot(next.workStartsAt, {
      travel: next.travel,
      work: next.work,
      departure: next.departure,
    });
    return {
      kind: 'entry',
      entryId: entry.id,
      dto: {
        travelStartedAt: instants.travelStartedAt || null,
        actualWorkStartsAt: instants.actualStart,
        actualEndsAt: instants.actualEnd,
        departureEndedAt: instants.departureEndedAt || null,
        ...technicianPatch,
        ...overrideFields(overrides),
      },
    };
  }

  return {
    kind: 'entry',
    entryId: entry.id,
    dto: {
      slot: {
        kind: 'SEGMENTED',
        workStartsAt: next.workStartsAt,
        travelMinutes: next.travel,
        workMinutes: next.work,
        departureMinutes: next.departure,
      },
      // A drag reshapes or moves the block, so it no longer fills the Schicht it was derived from —
      // the same rule the form applies when a duration is typed by hand. Left set, `allDay` would go
      // on suppressing the shift check and the Pause overlap for a Termin that is neither.
      // Appointment-only: `UpdateScheduleEntryUseCase` forwards `allDay` on that branch alone, and
      // no other entry type can carry the flag in the first place.
      ...(block.kind === 'APPOINTMENT' && entry.allDay ? { allDay: false } : {}),
      ...technicianPatch,
      ...overrideFields(overrides),
    },
  };
}
